import { ButtonProps, Button, IconButton } from '@nexelem/components';
import { ReactNode } from 'react';
import { toast } from 'react-toastify';
import { SnackbarColors, SnackbarToToastTypes } from '../types';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type ToastButton = Partial<Omit<ButtonProps, 'onClick'>> & {
  onClick?: () => void;
  label: string;
};

export type SnackbarToToastProps = {
  message: string | ReactNode;
  firstButton?: ToastButton;
  secondButton?: ToastButton;
  variantColor: SnackbarColors;
  type: SnackbarToToastTypes;
  withCloseButton?: boolean;
  id?: string;
  isJumbo?: boolean;
};

const snackbarColors = cva('', {
  variants: {
    bg: {
      green: 'bg-surface-status-green-darker',
      red: 'bg-surface-status-red-darker',
      grey: 'bg-surface-status-grey-darker',
      yellow: 'bg-surface-status-yellow',
    },
    text: {
      green: 'text-text-primary-inverse!',
      red: 'text-text-primary-inverse!',
      grey: 'text-text-primary-inverse!',
      yellow: 'text-text-primary!',
    },
    hover: {
      green: 'hover:bg-surface-hover-lighter/20',
      red: 'hover:bg-surface-hover-lighter/20',
      grey: 'hover:bg-surface-hover-lighter/20',
      yellow: 'hover:bg-surface-hover-lighter/20',
    },
    focus: {
      green: 'focus:bg-surface-hover-lighter/40!',
      red: 'focus:bg-surface-hover-lighter/40!',
      grey: 'focus:bg-surface-hover-lighter/40!',
      yellow: 'focus:bg-surface-hover-lighter/40!',
    },
  },
});

export const SnackbarToToast = ({
  message,
  variantColor,
  firstButton,
  secondButton,
  id: toastId,
  withCloseButton = true,
  type,
  isJumbo = false,
}: SnackbarToToastProps) => {
  const handleCloseSnackbar = () => {
    toast.dismiss(toastId);
  };

  const handleClick = (buttonProps: ToastButton) => {
    buttonProps.onClick?.();
    handleCloseSnackbar();
  };

  const handleFirstButtonClick = () => firstButton && handleClick(firstButton);
  const handleSecondButtonClick = () => secondButton && handleClick(secondButton);

  return (
    <div
      className={clsx(
        isJumbo ? 'min-h-[116px]' : 'min-h-[60px]',
        'flex items-center justify-between rounded-xs px-2 pl-6',
        snackbarColors({ bg: variantColor, text: variantColor }),
      )}
    >
      <p className={clsx(isJumbo ? 'text-s24' : 'text-s16', 'my-2 mr-4')}>{message}</p>
      {(!!firstButton || !!secondButton || withCloseButton) && (
        <div className="ml-2 flex shrink-0">
          {firstButton && (
            <Button
              className={clsx(snackbarColors({ text: variantColor, hover: variantColor, focus: variantColor }))}
              size={isJumbo ? 'jumbo' : 'medium'}
              variant="text"
              onClick={handleFirstButtonClick}
            >
              {firstButton.label}
            </Button>
          )}
          {secondButton && (
            <Button
              className={clsx(snackbarColors({ text: variantColor, hover: variantColor, focus: variantColor }))}
              size={isJumbo ? 'jumbo' : 'medium'}
              variant="text"
              onClick={handleSecondButtonClick}
            >
              {secondButton.label}
            </Button>
          )}
          {withCloseButton && (
            <div className="flex items-center">
              <IconButton
                Icon={() => <FontAwesomeIcon icon={faXmark} />}
                size={isJumbo ? 'large' : 'small'}
                className={clsx(
                  '[&_svg]:!fill-current [&_svg]:!text-inherit [&:focus_svg]:!text-inherit [&:hover_svg]:!text-inherit',
                  snackbarColors({ text: variantColor, hover: variantColor, focus: variantColor }),
                )}
                onClick={handleCloseSnackbar}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
